import { useState } from "react";
import { Check, X, ArrowRight, ChevronDown, ChevronUp, Gauge } from "lucide-react";

const green = "#1B3A1B";
const greenPale = "#E8F0E8";
const accent = "#8FBF6A";
const cream = "#FAF8F4";
const sand = "#F0EBE3";
const charcoal = "#2C2C2C";
const mush = "#A89880";

const SCANNER = "https://sendthemess.com/pages/mess-meter-tool";
const AUDIT = "https://sendthemess.com/pages/mini-mess-audit%E2%84%A2-shopify-catalog-audit-diy-fix-plan";
const INTAKE = "https://sendthemess.com/pages/send-the-mess-start-here";

export default function PricingPage() {
  const [openAddon, setOpenAddon] = useState<string | null>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const tiers = [
    {
      id: "audit",
      name: "Mini Mess Audit™",
      regular: "$297",
      founding: "$148.50",
      tag: "Start here",
      desc: "A full catalog review and an honest action plan. You walk away knowing exactly what is broken, what is actually fine, and what to do next.",
      includes: [
        "Full catalog review",
        "Product and listing issue review",
        "SEO and search readiness notes",
        "Collection and navigation recommendations",
        "Image and organization notes",
        "Missing info and assets checklist",
        "Your personalized Mess Map™: a step-by-step cleanup plan built for your exact catalog",
      ],
      notIncludes: [
        "Full CSV creation",
        "Product copywriting for every listing",
        "Product photo or mockup creation",
        "Shopify upload",
      ],
      note: "Audit fee is credited toward any build if you move forward. The Mess Map™ is yours to keep either way.",
      cta: "Get the Audit",
      ctaUrl: AUDIT,
      featured: false,
    },
    {
      id: "starter",
      name: "Starter Build™",
      regular: "$750",
      founding: "$375",
      tag: "Up to 20 products",
      desc: "For founders who are close to launch but buried in product chaos. You have the products. You have the store. You have a spreadsheet that has seen things.",
      includes: null,
      notIncludes: null,
      note: null,
      cta: "Send the Mess",
      ctaUrl: INTAKE,
      featured: false,
    },
    {
      id: "launch",
      name: "Launch Build™",
      regular: "$1,850",
      founding: "$925",
      tag: "Up to 50 products",
      desc: "The please-get-me-across-the-finish-line package. Deep cleanup, consistent naming, brand-aligned copy, and a launch-ready handoff.",
      includes: null,
      notIncludes: null,
      note: "Most founders land here.",
      cta: "Send the Mess",
      ctaUrl: INTAKE,
      featured: true,
    },
    {
      id: "full",
      name: "Full Catalog Build™",
      regular: "$3,250",
      founding: "$1,625",
      tag: "Up to 100 products",
      desc: "For larger stores, migrations, and catalogs that became sentient. Full architecture, complete organization pass, the works.",
      includes: null,
      notIncludes: null,
      note: null,
      cta: "Send the Mess",
      ctaUrl: INTAKE,
      featured: false,
    },
  ];

  const allBuildsInclude = [
    "Shopify-ready CSV",
    "Clean, consistent product titles",
    "Product descriptions written or cleaned up",
    "Brand-aligned voice and naming consistency",
    "Tags",
    "Product type and category guidance",
    "Collection mapping",
    "Variant cleanup",
    "Image order guidance",
    "SEO-ready product structure",
    "AI and GEO search-ready product data",
    "Launch-Ready Delivery Pack",
  ];

  const allBuildsExclude = [
    "Custom product photo or mockup creation",
    "Deep image and file sorting (add Image Organization)",
    "Uploading into Shopify (add Upload + Publish)",
    "Theme or store design",
    "Logo or packaging design",
    "Social graphics, ads, or video",
    "Unlimited revisions",
    "Emergency turnaround (add Rush Turnaround)",
  ];

  const deliveryPack = [
    "Final Shopify-ready CSV",
    "Collection map",
    "Image order guidance",
    "Missing detail notes",
    "Upload guidance",
    "Final pre-launch checklist",
    "Clear ready / needs-review handoff notes",
  ];

  const addons = [
    {
      id: "upload",
      name: "Shopify Upload + Publish Add-On™",
      price: "$250",
      tag: "Requires temporary store access",
      desc: "We upload the finished CSV into your Shopify store and spot-check the live product pages so you do not have to.",
      includes: ["CSV upload into Shopify", "Import issue review", "Product page spot-checking", "Variant checks", "Collection checks", "Image order checks", "Notes on anything still needing attention"],
      excludes: ["Theme or store redesign", "App or checkout setup", "Policy writing", "Advanced technical fixes"],
    },
    {
      id: "images",
      name: "Image Organization Add-On™",
      price: "$150 to $350",
      tag: "For extreme file chaos",
      desc: "Basic image order guidance is included in every build. This is for founders whose files are genuinely beyond normal intake.",
      includes: ["Sorting messy folders", "Matching images to products", "Renaming files", "Organizing product assets", "Flagging duplicates", "Flagging missing images", "Cleaner file structure for handoff"],
      excludes: ["Creating new product photos", "Editing or redesigning images", "Cloud storage cleanup outside project scope"],
    },
    {
      id: "rush",
      name: "Rush Turnaround",
      price: "+20%",
      tag: "When you needed it yesterday",
      desc: "Front of the queue when a slot is available. Launch is next week and past-you made some decisions. We get it.",
      includes: ["Expedited project timeline", "Priority placement in the work queue"],
      excludes: ["Expanded scope or extra products", "Product visuals", "Upload or QA unless separately added"],
    },
  ];

  const faqs = [
    { q: "What exactly is the founding client offer?", a: "The first three clients get 50% off one core Audit or Build. In exchange, you give us permission to build and share a case study from your project, you give us honest feedback as we work, and you leave an honest review. Not a glowing review. An honest one. This is not a sale or a discount promotion. These three founders are literally helping us build and stress-test the workflow, and the 50% is how we say thank you." },
    { q: "Do add-ons get the founding discount too?", a: "No. Add-ons stay at full price. The 50% applies to one core Audit or Build only." },
    { q: "What happens after the three spots are gone?", a: "Pricing goes back to standard and stays there. This is not a rolling promotion and it will not come back around." },
    { q: "What if my honest review is not glowing?", a: "Then it is not glowing. We are asking for honest feedback specifically because we want to find the gaps in the process while the stakes are low. A polite lie helps nobody." },
    { q: "Do I need the audit before a build?", a: "Nope, but it helps. If you already know your catalog is a mess and you are ready to fix it, go straight to a build. If you are not sure what is broken, the audit gives you a clear picture first and the fee gets credited if you move forward." },
    { q: "What is the free Mess Meter™?", a: "It is a free scanner. Upload your Shopify product CSV and get an instant breakdown of what is broken: orphaned products, broken variants, missing data, duplicate rows, empty fields. It costs nothing and it takes about a minute." },
    { q: "What does launch-ready actually mean?", a: "Your catalog handoff is organized, structured, search-ready, and upload-ready. It does not mean we design your whole store, set up your theme, or write your policies. It means your products are finally ready to go live." },
    { q: "What do I need to send you?", a: "Whatever you have. Spreadsheets, Shopify exports, Google Sheets, Notion docs, supplier sheets, a folder named FINAL FINAL USE THIS. Do not spend time organizing before you send it. That is literally our job." },
  ];

  return (
    <div style={{ fontFamily: "'Cabin', system-ui, sans-serif", background: cream, color: charcoal, minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cabin:wght@400;500;600;700&family=Kalam:wght@700&display=swap');
        * { box-sizing: border-box; }
        .kalam { font-family: 'Kalam', cursive; }
        .card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .card:hover { transform: translateY(-3px); box-shadow: 0 12px 32px rgba(27,58,27,0.12); }
        .btn { transition: all 0.2s ease; text-decoration: none; display: inline-flex; align-items: center; justify-content: center; gap: 8px; }
        .btn:hover { opacity: 0.92; transform: translateY(-1px); }
        .toggle { cursor: pointer; transition: all 0.2s ease; }
        .toggle:hover { background: ${greenPale} !important; }
        @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
        .dot { animation: pulse 2.2s ease-in-out infinite; }
        @media (max-width: 760px) {
          .tier-grid { grid-template-columns: 1fr !important; }
          .two-col { grid-template-columns: 1fr !important; }
          .btn-row { flex-direction: column !important; }
          .btn-row > a { width: 100% !important; }
        }
      `}</style>

      {/* FOUNDING BANNER */}
      <div style={{ background: accent, color: green, padding: "10px 20px", textAlign: "center", fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
        <span className="dot" style={{ width: 7, height: 7, borderRadius: "50%", background: green, display: "inline-block" }} />
        3 founding client spots. 50% off one core Audit or Build. This is not a sale.
      </div>

      {/* NAV */}
      <nav style={{ background: green, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, zIndex: 50, gap: 12 }}>
        <a href="https://sendthemess.com" style={{ color: "white", fontWeight: 700, fontSize: 15, textDecoration: "none", whiteSpace: "nowrap" }}>
          Send <span style={{ color: accent }}>the</span> Mess
        </a>
        <a href={SCANNER} className="btn" style={{ background: accent, color: green, padding: "9px 16px", fontWeight: 700, fontSize: 11.5, letterSpacing: "0.06em", textTransform: "uppercase", borderRadius: 4, whiteSpace: "nowrap" }}>
          <Gauge size={13} /> Free Mess Meter™
        </a>
      </nav>

      {/* HERO */}
      <section style={{ background: green, padding: "64px 20px 76px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div style={{ maxWidth: 780, margin: "0 auto", position: "relative" }}>
          <div className="kalam" style={{ color: accent, fontSize: 17, marginBottom: 14 }}>real prices, no hourly mystery billing</div>
          <h1 style={{ color: "white", fontSize: "clamp(31px, 5vw, 52px)", fontWeight: 700, lineHeight: 1.1, margin: "0 0 20px", letterSpacing: "-0.02em" }}>
            Your catalog is a mess.<br />
            <span style={{ color: accent }}>Here is what it costs to fix it.</span>
          </h1>
          <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 17, lineHeight: 1.7, maxWidth: 560, margin: "0 auto 32px" }}>
            Every package is done-for-you, Shopify-specific, and built around one promise: a clean, organized, launch-ready catalog handoff. Right now the first three clients get half off.
          </p>
          <div className="btn-row" style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <a href={SCANNER} className="btn" style={{ background: accent, color: green, padding: "15px 26px", fontWeight: 700, fontSize: 13.5, borderRadius: 4, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              <Gauge size={15} /> Scan My Catalog Free
            </a>
            <a href={AUDIT} className="btn" style={{ background: "transparent", color: "white", padding: "15px 26px", fontWeight: 700, fontSize: 13.5, border: "1px solid rgba(255,255,255,0.35)", borderRadius: 4, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              Get the Mini Mess Audit™
            </a>
          </div>
          <div style={{ marginTop: 36, display: "flex", gap: 26, justifyContent: "center", flexWrap: "wrap" }}>
            {[["🛒", "Shopify only"], ["👤", "Human reviewed"], ["📦", "Launch-ready delivery"], ["⚡", "SEO and AI-ready"]].map(([icon, label]) => (
              <div key={label} style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, display: "flex", alignItems: "center", gap: 7 }}>
                <span>{icon}</span>{label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MESS METER PROMO */}
      <section style={{ background: sand, padding: "56px 20px", borderBottom: `1px solid #E0D8CF` }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div className="two-col" style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 40, alignItems: "center" }}>
            <div>
              <div className="kalam" style={{ color: green, fontSize: 16, marginBottom: 10 }}>start here, it is free</div>
              <h2 style={{ fontSize: "clamp(25px, 4vw, 34px)", fontWeight: 700, color: green, margin: "0 0 14px", lineHeight: 1.2 }}>
                Do not guess how bad it is.<br />Run the Mess Meter™.
              </h2>
              <p style={{ fontSize: 15, lineHeight: 1.75, color: charcoal, margin: "0 0 14px", opacity: 0.85 }}>
                Upload your Shopify product CSV and get an instant, detailed breakdown of everything hiding in your rows. It takes about a minute, it costs nothing, and you get the full report immediately.
              </p>
              <p style={{ fontSize: 15, lineHeight: 1.75, color: charcoal, margin: "0 0 24px", opacity: 0.85 }}>
                Most founders run it expecting a handful of problems and then sit very quietly for a moment.
              </p>
              <a href={SCANNER} className="btn" style={{ background: green, color: "white", padding: "15px 28px", fontWeight: 700, fontSize: 13.5, borderRadius: 4, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                <Gauge size={15} /> Run the Free Mess Meter™ <ArrowRight size={14} />
              </a>
            </div>
            <div style={{ background: "white", border: `1px solid #E0D8CF`, borderRadius: 8, padding: 26, boxShadow: "0 6px 20px rgba(0,0,0,0.05)" }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: green, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 16 }}>Your free report shows</div>
              {[
                "Orphaned products with no collection home",
                "Broken and incomplete variants",
                "Missing prices, SKUs, and required fields",
                "Duplicate and near-duplicate rows",
                "Empty or placeholder descriptions",
                "Image gaps and mismatches",
                "A full row-by-row damage breakdown",
              ].map((item) => (
                <div key={item} style={{ display: "flex", gap: 9, alignItems: "flex-start", marginBottom: 10, fontSize: 13.5, color: charcoal }}>
                  <Check size={13} color={accent} style={{ marginTop: 3, flexShrink: 0 }} />{item}
                </div>
              ))}
              <div style={{ marginTop: 16, padding: "11px 14px", background: greenPale, borderRadius: 5, fontSize: 12.5, color: green, fontWeight: 700, textAlign: "center" }}>
                Free. Instant. No sales call.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDING CLIENT EXPLAINER */}
      <section style={{ background: green, padding: "64px 20px" }}>
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 36 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: accent, color: green, padding: "6px 16px", borderRadius: 20, fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 18 }}>
              <span className="dot" style={{ width: 6, height: 6, borderRadius: "50%", background: green }} />
              3 spots only
            </div>
            <h2 style={{ color: "white", fontSize: "clamp(26px, 4vw, 38px)", fontWeight: 700, lineHeight: 1.2, margin: "0 0 16px" }}>
              This is not a sale.<br /><span style={{ color: accent }}>It is a trade.</span>
            </h2>
            <p style={{ color: "rgba(255,255,255,0.78)", fontSize: 16, lineHeight: 1.75, maxWidth: 620, margin: "0 auto" }}>
              Three founding clients get 50% off one core Audit or Build. Not because the work is worth less. Because these three founders are going to help us build and stress-test the workflow that everyone after them gets to benefit from. The 50% is how we say thank you.
            </p>
          </div>

          <div className="two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28 }}>
            <div style={{ background: "rgba(255,255,255,0.05)", border: `1px solid rgba(143,191,106,0.3)`, borderRadius: 8, padding: 26 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: accent, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 16 }}>What you give</div>
              {[
                "Permission to build and share a case study from your project",
                "Honest feedback while we work, not polite feedback",
                "An honest review at the end, glowing or not",
              ].map((item) => (
                <div key={item} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12, fontSize: 14, color: "rgba(255,255,255,0.85)", lineHeight: 1.6 }}>
                  <ArrowRight size={13} color={accent} style={{ marginTop: 4, flexShrink: 0 }} />{item}
                </div>
              ))}
            </div>
            <div style={{ background: "rgba(255,255,255,0.05)", border: `1px solid rgba(143,191,106,0.3)`, borderRadius: 8, padding: 26 }}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: accent, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 16 }}>What you get</div>
              {[
                "50% off one core Audit or Build",
                "Direct input on how the workflow gets built",
                "The same full-scope deliverables as everyone else",
                "Founding client credit on the case study, if you want it",
              ].map((item) => (
                <div key={item} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12, fontSize: 14, color: "rgba(255,255,255,0.85)", lineHeight: 1.6 }}>
                  <Check size={13} color={accent} style={{ marginTop: 4, flexShrink: 0 }} />{item}
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: "rgba(143,191,106,0.12)", border: `1px solid rgba(143,191,106,0.35)`, borderRadius: 6, padding: "16px 20px", textAlign: "center" }}>
            <p style={{ color: "rgba(255,255,255,0.9)", fontSize: 14, lineHeight: 1.7, margin: 0 }}>
              <strong style={{ color: "white" }}>The 50% applies to one core Audit or Build only.</strong> Add-ons stay at full price. Once the three spots are gone, pricing goes back to standard and stays there.
            </p>
          </div>
        </div>
      </section>

      {/* PRICING TIERS */}
      <section style={{ padding: "64px 20px", background: cream }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <h2 style={{ fontSize: "clamp(26px, 4vw, 36px)", fontWeight: 700, color: green, margin: "0 0 10px" }}>Pick your package.</h2>
            <p style={{ fontSize: 16, color: mush, margin: 0 }}>Founding pricing shown. Not sure where to start? Run the free scanner first.</p>
          </div>

          <div className="tier-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18, alignItems: "start" }}>
            {tiers.map((tier) => (
              <div key={tier.id} className="card" style={{
                background: tier.featured ? green : "white",
                border: `2px solid ${tier.featured ? accent : "#E0D8CF"}`,
                borderRadius: 8, padding: "28px 24px", position: "relative",
                boxShadow: tier.featured ? "0 16px 44px rgba(27,58,27,0.2)" : "0 4px 16px rgba(0,0,0,0.05)",
              }}>
                {tier.featured && (
                  <div style={{ position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: accent, color: green, padding: "3px 14px", borderRadius: 20, fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap" }}>
                    Most Popular
                  </div>
                )}

                <div style={{ fontSize: 10.5, fontWeight: 700, color: accent, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 10 }}>{tier.tag}</div>
                <h3 style={{ fontSize: 19, fontWeight: 700, color: tier.featured ? "white" : green, margin: "0 0 12px", lineHeight: 1.25 }}>{tier.name}</h3>

                {/* PRICE BLOCK */}
                <div style={{ marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 33, fontWeight: 700, color: tier.featured ? accent : green, lineHeight: 1 }}>{tier.founding}</span>
                    <span style={{ fontSize: 17, color: tier.featured ? "rgba(255,255,255,0.4)" : mush, textDecoration: "line-through", fontWeight: 600 }}>{tier.regular}</span>
                  </div>
                  <div style={{
                    display: "inline-block", marginTop: 8,
                    background: tier.featured ? "rgba(143,191,106,0.2)" : greenPale,
                    color: tier.featured ? accent : green,
                    border: `1px solid ${tier.featured ? "rgba(143,191,106,0.4)" : accent}`,
                    padding: "3px 9px", borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase"
                  }}>
                    Founding client price
                  </div>
                </div>

                <p style={{ fontSize: 13, lineHeight: 1.65, color: tier.featured ? "rgba(255,255,255,0.8)" : charcoal, margin: "16px 0 18px" }}>{tier.desc}</p>

                {tier.includes && (
                  <>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: tier.featured ? accent : green, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Includes</div>
                    <ul style={{ listStyle: "none", padding: 0, margin: "0 0 16px" }}>
                      {tier.includes.map((item) => (
                        <li key={item} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8, fontSize: 12.5, color: tier.featured ? "rgba(255,255,255,0.85)" : charcoal, lineHeight: 1.5 }}>
                          <Check size={12} color={accent} style={{ marginTop: 3, flexShrink: 0 }} />{item}
                        </li>
                      ))}
                    </ul>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: mush, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10 }}>Not included</div>
                    <ul style={{ listStyle: "none", padding: 0, margin: "0 0 16px" }}>
                      {tier.notIncludes?.map((item) => (
                        <li key={item} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 6, fontSize: 12, color: mush, lineHeight: 1.5 }}>
                          <X size={11} color="#D0C0B0" style={{ marginTop: 3, flexShrink: 0 }} />{item}
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {!tier.includes && (
                  <div style={{ padding: "13px 15px", background: tier.featured ? "rgba(255,255,255,0.07)" : greenPale, borderRadius: 6, marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: tier.featured ? accent : green, fontWeight: 600, marginBottom: 4, lineHeight: 1.4 }}>Everything in "What's included in every build" below</div>
                    <div style={{ fontSize: 12, color: tier.featured ? "rgba(255,255,255,0.6)" : mush }}>Plus the Launch-Ready Delivery Pack</div>
                  </div>
                )}

                {tier.note && (
                  <p style={{ fontSize: 11.5, color: tier.featured ? "rgba(255,255,255,0.55)" : mush, margin: "0 0 16px", fontStyle: "italic", lineHeight: 1.6 }}>{tier.note}</p>
                )}

                <a href={tier.ctaUrl} className="btn" style={{
                  background: tier.featured ? accent : green, color: tier.featured ? green : "white",
                  padding: "13px 18px", borderRadius: 4, fontWeight: 700, fontSize: 12.5,
                  width: "100%", letterSpacing: "0.05em", textTransform: "uppercase"
                }}>
                  {tier.cta} <ArrowRight size={13} />
                </a>
              </div>
            ))}
          </div>

          <p style={{ textAlign: "center", marginTop: 28, fontSize: 13, color: mush, maxWidth: 560, marginLeft: "auto", marginRight: "auto", lineHeight: 1.7 }}>
            Founding client pricing applies to one core Audit or Build. Add-ons below remain at full price for everyone.
          </p>
        </div>
      </section>

      {/* MESS MAP */}
      <section style={{ padding: "64px 20px", background: sand }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div className="two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 44, alignItems: "center" }}>
            <div>
              <div className="kalam" style={{ color: green, fontSize: 16, marginBottom: 10 }}>included with every Mini Mess Audit™</div>
              <h2 style={{ color: green, fontSize: "clamp(25px, 4vw, 36px)", fontWeight: 700, lineHeight: 1.2, margin: "0 0 16px" }}>
                Your personalized Mess Map™.
              </h2>
              <p style={{ color: charcoal, fontSize: 15, lineHeight: 1.75, margin: "0 0 14px", opacity: 0.85 }}>
                A clear, follow-it-yourself cleanup guide built specifically for your catalog, your blockers, and your urgency level. Print it out, work through it at the kitchen table, check things off one by one, or just follow it on screen. Either way it is yours.
              </p>
              <p style={{ color: charcoal, fontSize: 15, lineHeight: 1.75, margin: "0 0 20px", opacity: 0.85 }}>
                It is not a generic checklist. It tells you exactly what to do, in what order, and why. If you decide to handle the cleanup yourself, you actually can.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
                <div style={{ padding: "13px 16px", background: "white", borderRadius: 6, border: `1px solid #E0D8CF` }}>
                  <div style={{ fontSize: 12.5, color: green, fontWeight: 700, marginBottom: 3 }}>If you do it yourself</div>
                  <div style={{ fontSize: 13, color: charcoal, lineHeight: 1.6, opacity: 0.8 }}>You leave with a real plan you can actually follow. No guessing. No starting over.</div>
                </div>
                <div style={{ padding: "13px 16px", background: "white", borderRadius: 6, border: `1px solid #E0D8CF` }}>
                  <div style={{ fontSize: 12.5, color: green, fontWeight: 700, marginBottom: 3 }}>If you hire us for a build</div>
                  <div style={{ fontSize: 13, color: charcoal, lineHeight: 1.6, opacity: 0.8 }}>The audit fee is credited toward your package. You pay less. We already know your catalog. We start faster.</div>
                </div>
              </div>
              <a href={AUDIT} className="btn" style={{ background: green, color: "white", padding: "14px 26px", fontWeight: 700, fontSize: 13, borderRadius: 4, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                Get the Mini Mess Audit™ <ArrowRight size={13} />
              </a>
            </div>
            <div style={{ background: green, borderRadius: 8, padding: 30 }}>
              <div className="kalam" style={{ color: accent, fontSize: 15, marginBottom: 18 }}>what is in your Mess Map™</div>
              {[
                "Your urgency scale: how critical this actually is and what waiting is costing you",
                "Your priority order: exactly what to fix first, second, and third",
                "A cleanup path written for your product count and file situation",
                "Specific action steps for each of your actual blockers",
                "File and image organization instructions for your specific mess",
                "An honest assessment of when to hire help, with no pressure attached",
                "A launch readiness checklist you can tick off as you go",
              ].map((item) => (
                <div key={item} style={{ display: "flex", gap: 9, alignItems: "flex-start", marginBottom: 11, fontSize: 13.5, color: "rgba(255,255,255,0.85)", lineHeight: 1.55 }}>
                  <Check size={13} color={accent} style={{ marginTop: 3, flexShrink: 0 }} />{item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section style={{ padding: "64px 20px", background: cream }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 44 }}>
            <div className="kalam" style={{ color: green, fontSize: 16, marginBottom: 8 }}>no surprises</div>
            <h2 style={{ fontSize: "clamp(24px, 4vw, 34px)", fontWeight: 700, color: green, margin: "0 0 10px" }}>What's included in every build.</h2>
            <p style={{ fontSize: 15, color: mush, maxWidth: 480, margin: "0 auto" }}>Every Starter, Launch, and Full Catalog Build includes all of this.</p>
          </div>

          <div className="two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 }}>
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: green, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 16 }}>Always included</div>
              {allBuildsInclude.map((item) => (
                <div key={item} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 11, fontSize: 14, color: charcoal }}>
                  <Check size={14} color={accent} style={{ marginTop: 2, flexShrink: 0 }} />{item}
                </div>
              ))}
            </div>
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: mush, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 16 }}>Not in the base build</div>
              {allBuildsExclude.map((item) => (
                <div key={item} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 11, fontSize: 14, color: mush }}>
                  <X size={13} color="#D0C0B0" style={{ marginTop: 2, flexShrink: 0 }} />{item}
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 44, background: green, borderRadius: 8, padding: "30px 32px" }}>
            <div className="two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 26 }}>
              <div>
                <div className="kalam" style={{ color: accent, fontSize: 15, marginBottom: 8 }}>included with every build</div>
                <h3 style={{ color: "white", fontSize: 21, fontWeight: 700, margin: "0 0 10px" }}>Launch-Ready Delivery Pack</h3>
                <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 14, lineHeight: 1.65, margin: 0 }}>
                  This is not an add-on. Every main build comes with a complete handoff package so you know exactly what is ready, what still needs attention, and how to move forward.
                </p>
              </div>
              <div>
                {deliveryPack.map((item) => (
                  <div key={item} style={{ display: "flex", gap: 9, alignItems: "flex-start", marginBottom: 9, fontSize: 13.5, color: "rgba(255,255,255,0.85)" }}>
                    <Check size={13} color={accent} style={{ marginTop: 3, flexShrink: 0 }} />{item}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SCOPE NOTES */}
          <div className="two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginTop: 32 }}>
            <div style={{ background: sand, borderRadius: 6, padding: "20px 22px", borderLeft: `3px solid ${accent}` }}>
              <div className="kalam" style={{ color: green, fontSize: 14, marginBottom: 6 }}>plain english scope note</div>
              <p style={{ fontSize: 14, lineHeight: 1.7, color: charcoal, margin: 0 }}>
                <strong>Your build includes image guidance.</strong> It does not include creating brand-new product visuals or deep file cleanup unless you add the Image Organization Add-On™.
              </p>
            </div>
            <div style={{ background: sand, borderRadius: 6, padding: "20px 22px", borderLeft: `3px solid ${accent}` }}>
              <div className="kalam" style={{ color: green, fontSize: 14, marginBottom: 6 }}>what "launch-ready" means</div>
              <p style={{ fontSize: 14, lineHeight: 1.7, color: charcoal, margin: 0 }}>
                Your catalog handoff is organized, structured, search-ready, and upload-ready. It does not mean we design your whole store.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ADD-ONS */}
      <section style={{ padding: "64px 20px", background: sand }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div className="kalam" style={{ color: green, fontSize: 16, marginBottom: 8 }}>need a little extra?</div>
            <h2 style={{ fontSize: "clamp(24px, 4vw, 34px)", fontWeight: 700, color: green, margin: "0 0 10px" }}>Add-ons</h2>
            <p style={{ fontSize: 15, color: mush, maxWidth: 500, margin: "0 auto", lineHeight: 1.7 }}>
              Stack these onto any package. Add-ons are always full price, including for founding clients.
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {addons.map((addon) => (
              <div key={addon.id} style={{ background: "white", border: `1px solid #E0D8CF`, borderRadius: 6, overflow: "hidden" }}>
                <div className="toggle" onClick={() => setOpenAddon(openAddon === addon.id ? null : addon.id)}
                  style={{ padding: "18px 22px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, background: openAddon === addon.id ? greenPale : "white" }}>
                  <div style={{ display: "flex", gap: 16, alignItems: "center", flex: 1, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14.5, color: green }}>{addon.name}</div>
                      <div style={{ fontSize: 12, color: mush, marginTop: 2 }}>{addon.tag}</div>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: accent }}>{addon.price}</div>
                  </div>
                  {openAddon === addon.id ? <ChevronUp size={18} color={green} style={{ flexShrink: 0 }} /> : <ChevronDown size={18} color={mush} style={{ flexShrink: 0 }} />}
                </div>
                {openAddon === addon.id && (
                  <div style={{ padding: "0 22px 22px", borderTop: `1px solid #E0D8CF` }}>
                    <p style={{ fontSize: 14, lineHeight: 1.7, color: charcoal, margin: "16px 0 18px" }}>{addon.desc}</p>
                    <div className="two-col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22 }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: green, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 10 }}>Includes</div>
                        {addon.includes.map((item) => (
                          <div key={item} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 7, fontSize: 13, color: charcoal }}>
                            <Check size={12} color={accent} style={{ marginTop: 3, flexShrink: 0 }} />{item}
                          </div>
                        ))}
                      </div>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: mush, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 10 }}>Does not include</div>
                        {addon.excludes.map((item) => (
                          <div key={item} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 7, fontSize: 13, color: mush }}>
                            <X size={12} color="#D0C0B0" style={{ marginTop: 3, flexShrink: 0 }} />{item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: "64px 20px", background: cream }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <h2 style={{ fontSize: "clamp(24px, 4vw, 34px)", fontWeight: 700, color: green, margin: 0 }}>Questions founders actually ask.</h2>
          </div>
          {faqs.map((faq, i) => (
            <div key={i} style={{ borderBottom: `1px solid #E0D8CF` }}>
              <div onClick={() => setOpenFaq(openFaq === i ? null : i)}
                style={{ padding: "18px 0", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", gap: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 15, color: green }}>{faq.q}</div>
                {openFaq === i ? <ChevronUp size={17} color={green} style={{ flexShrink: 0 }} /> : <ChevronDown size={17} color={mush} style={{ flexShrink: 0 }} />}
              </div>
              {openFaq === i && (
                <p style={{ fontSize: 14, lineHeight: 1.75, color: charcoal, margin: "0 0 18px", opacity: 0.85 }}>{faq.a}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section style={{ background: green, padding: "80px 20px", textAlign: "center" }}>
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: accent, color: green, padding: "6px 16px", borderRadius: 20, fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 20 }}>
            <span className="dot" style={{ width: 6, height: 6, borderRadius: "50%", background: green }} />
            3 founding spots left
          </div>
          <h2 style={{ color: "white", fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 700, lineHeight: 1.15, margin: "0 0 16px" }}>
            Not sure how bad it is?<br />
            <span style={{ color: accent }}>Find out for free in about a minute.</span>
          </h2>
          <p style={{ color: "rgba(255,255,255,0.72)", fontSize: 16, lineHeight: 1.75, margin: "0 auto 32px", maxWidth: 500 }}>
            Run the Mess Meter™ on your product CSV first. If the report scares you, the audit is the next step. If you already know it is bad and you just want it handled, start your project.
          </p>
          <div className="btn-row" style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginBottom: 18 }}>
            <a href={SCANNER} className="btn" style={{ background: accent, color: green, padding: "16px 28px", fontWeight: 700, fontSize: 13.5, borderRadius: 4, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              <Gauge size={15} /> Run the Free Mess Meter™
            </a>
            <a href={AUDIT} className="btn" style={{ background: "transparent", color: "white", padding: "16px 28px", border: "1px solid rgba(255,255,255,0.35)", fontWeight: 700, fontSize: 13.5, borderRadius: 4, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              Get the Mini Mess Audit™
            </a>
          </div>
          <a href={INTAKE} className="btn" style={{ color: accent, fontSize: 14, fontWeight: 700, textDecoration: "underline", textUnderlineOffset: 4 }}>
            Or start your project now <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background: "#111C11", padding: "34px 20px", textAlign: "center" }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: "white", marginBottom: 6 }}>
          Send <span style={{ color: accent }}>the</span> Mess
        </div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.35)" }}>Scattered in. Organized out. Ready to launch.</div>
      </footer>
    </div>
  );
}